<script setup lang="ts">
import {
    CustomizableDatePicker,
    type Page,
    type Day,
    type InputValue,
    type RangeValue,
} from 'v-customizable-date-picker';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import 'v-customizable-date-picker/dist/v-customizable-date-picker.css';
import holidays from '@static/holidays.json';
import { type DayConfig } from '#imports';

const {
    dayConfig: dayConfigLocal = {},
    max,
    min,
    loading,
    disableMobileMonthList,
    monthCount = 1,
    range,
    rangeEndDayTooltip,
    readOnly,
    rangeStartDayTooltip,
    currentCalendar = 0,
} = defineProps<IProps>();
const emit = defineEmits<{
    drag: [];
    pageChange: [lastPage: Page];
}>();
const modelValue = defineModel<InputValue>({ default: null });
const currentSearchForm = useCurrentSearchForm();
const datepicker = ref<InstanceType<typeof CustomizableDatePicker>>();
interface IProps {
    min?: Date;
    max?: Date;
    loading?: boolean;
    currentCalendar?: number;
    monthCount?: number;
    range?: boolean;
    readOnly?: boolean;
    disableMobileMonthList?: boolean;
    dayConfig?: Record<string, DayConfig>;
    rangeStartDayTooltip?: string;
    rangeEndDayTooltip?: string;
    unequalRange?: boolean;
}
const gtm = useGtm();
const { isMobile } = useSize();
const currentHoveredDay = ref<Date>();
const calendar = computed(() =>
    currentCalendar ? gregorianCalendar : jalaliCalendar
);
const holidayMap = computed(() => {
    const obj: Record<number, 1> = {};
    holidays.forEach(element => {
        obj[dayConfig.gregory(element).unix()] = 1;
    });
    return obj;
});
const tooltip = computed(() => {
    if (!showReturnHints.value) {
        return `تاریخ ${rangeStartDayTooltip}`;
    } else {
        return `تاریخ ${rangeEndDayTooltip}`;
    }
});
const showReturnHints = computed(() => {
    const val = modelValue.value as RangeValue;
    return (
        range &&
        !!val.start &&
        !val.end &&
        !(
            currentHoveredDay.value &&
            calendar.value.isAfter(val.start, currentHoveredDay.value)
        )
    );
});
const onDayClick = (day: Day) => {
    const config = getConfig(day);
    gtm?.trackEvent({
        event: 'calendar',
        calendarDate: calendar.value
            .getDate(day.year, day.month, day.dayInMonth)
            .toISOString(),
        ...config.eventData,
    });
};
const isHoliday = (_day: Day) => {
    let holiday: number;
    switch (calendar.value) {
        case gregorianCalendar:
            holiday = 0;
            break;
        case jalaliCalendar:
        default:
            holiday = 5;
            break;
    }
    if (_day.dayInWeek === holiday) {
        return true;
    }
    const timestamp = dayConfig
        .gregory(calendar.value.getDate(_day.year, _day.month, _day.dayInMonth))
        .unix();
    return !!holidayMap.value[timestamp];
};
const getConfig = (_day: Day): DayConfig => {
    const key = dayConfig
        .gregory(calendar.value.getDate(_day.year, _day.month, _day.dayInMonth))
        .format(formatter());
    const config = dayConfigLocal[key] || {};
    return {
        ...config,
        color: config.color || {},
        background: config.background || {},
        labelColor: config.labelColor || {},
    };
};
watch(showReturnHints, val => {
    currentSearchForm.value.priceLoader?.setCurrentSelectionStep(
        val ? 'return' : 'departure'
    );
});
defineExpose({
    prev: () => datepicker.value?.prev(),
    next: () => datepicker.value?.next(),
    appendMonth: () => datepicker.value?.appendMonth(),
    prependMonth: () => datepicker.value?.prependMonth(),
});
</script>

<template>
    <CustomizableDatePicker
        ref="datepicker"
        :key="currentCalendar"
        v-model="modelValue"
        :calendars="[jalaliCalendar, gregorianCalendar]"
        class="mrbilit-date-picker"
        :style="{ '--tooltip': `'${tooltip}'` }"
        :class="{ 'mobile-list': !disableMobileMonthList }"
        :current-calendar
        :month-count
        :range
        :min="min ?? null"
        :max="max ?? null"
        :unequal-range
        :read-only
        :start-month-from-now="isMobile && !disableMobileMonthList"
        track-start
        @drag="emit('drag')"
        @page-change="emit('pageChange', $event)"
        @day-hover="currentHoveredDay = $event"
        @day-click="onDayClick"
    >
        <template #header-next-button>
            <mr-button
                variant="transparent"
                size="small"
                :right-icon="faAngleLeft"
            >
                ماه بعد
            </mr-button>
        </template>
        <template #header-prev-button>
            <mr-button
                variant="transparent"
                size="small"
                :left-icon="faAngleRight"
            >
                ماه قبل
            </mr-button>
        </template>
        <template #day-container="{ day: day1 }">
            <div
                v-for="config in { config: getConfig(day1) }"
                :key="config.label"
                class="day-wrapper"
                :class="[
                    {
                        selected: day1.isSelected,
                        between: day1.isBetween,
                        disable: day1.disabled,
                        'start-range': day1.startRange && !day1.endRange,
                        'end-range': day1.endRange && !day1.startRange,
                        today: day1.today,
                        'have-tooltip': !readOnly,
                    },
                ]"
                :style="{
                    '--badge-color': config.badgeColor,
                    '--color': config.color?.main,
                    '--color-hover': config.color?.hover,
                    '--color-selected': config.color?.selected,
                    '--background': config.background?.main,
                    '--background-hover': config.background?.hover,
                    '--background-selected': config.background?.selected,
                    '--label': config.labelColor?.main,
                    '--label-hover': config.labelColor?.hover,
                    '--label-selected': config.labelColor?.selected,
                }"
            >
                <div class="day-container">
                    <span
                        v-if="config.badgeColor || isHoliday(day1)"
                        class="badge"
                    />
                    {{ day1.dayInMonth }}
                    <mr-fade-loading v-if="loading && !day1.disabled" />
                    <mr-icon
                        v-else-if="config.icon"
                        :icon="config.icon"
                        size="x-small"
                        class="day-icon"
                        :color="
                            day1.isSelected
                                ? config.iconColor?.selected
                                : day1.isBetween
                                  ? config.iconColor?.hover
                                  : config.iconColor?.main
                        "
                    />
                    <div v-else class="label">
                        {{ config.label || '&nbsp;' }}
                    </div>
                </div>
            </div>
        </template>
    </CustomizableDatePicker>
</template>

<style lang="scss" scoped>
$gap: 4px;
$cell-size: 44px;

.day-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    box-sizing: border-box;
    cursor: pointer;
    height: $cell-size;
    width: $cell-size;
    margin-bottom: $gap;
    &.selected {
        .day-container {
            background: var(--background-selected, usePalette());
            color: var(--color-selected, white);
            .label {
                color: var(--label-selected, white);
            }
        }
    }

    &.have-tooltip:hover:not(.disable) {
        position: relative;
        &::before {
            content: var(--tooltip);
            position: absolute;
            top: -40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 0, 0, 0.7);
            width: 79px;
            height: 32px;
            border-radius: 8px;
            z-index: 10;
            opacity: 1;
            // text
            @include tg-s($w5, white);
        }
        &::after {
            content: ' ';
            position: absolute;
            top: -10px;
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 8px solid rgba(0, 0, 0, 0.7);
            z-index: 10;
        }
    }

    &:hover:not(.selected):not(.disable),
    &.between {
        .day-container {
            background: var(--background-hover, usePalette($p-blue, $p-300));
            width: 100%;
            color: var(--color-hover, white);
            .label {
                color: var(--label-hover, white);
            }
            .day-icon {
                :deep(svg) {
                    color: white !important;
                }
            }
        }
    }
    &.between {
        padding: 0;
        border-radius: 0;
        .day-container {
            margin-inline: 0;
            border-radius: 0;
        }
    }
    &.start-range {
        &,
        & .day-container {
            border-radius: 0 10px 10px 0;
        }
    }
    &.end-range {
        &,
        & .day-container {
            border-radius: 10px 0 0 10px;
        }
    }

    &.today {
        .day-container {
            border: 1px solid usePalette();
        }
    }
    &.disable {
        cursor: unset;
        .day-container {
            border: none;
            color: usePalette($p-gray, $p-500) !important;
            text-decoration: line-through;
            .label {
                visibility: hidden;
            }
        }
    }

    .day-container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        height: $cell-size;
        width: $cell-size;
        flex-grow: 1;
        box-sizing: border-box;
        border-radius: 4px;
        z-index: 1;
        user-select: none;
        background-color: var(--background, usePalette($p-blue, $p-100));
        // text
        @include tg-l($w7, var(--color, usePalette($p-cool-gray, $p-800)));
        line-height: 10px;
        .label {
            max-width: 100%;
            overflow: hidden;
            // text
            @include tg-xs($w5, var(--label, usePalette($p-cool-gray, $p-600)));
            line-height: 22px;
            white-space: nowrap;
            text-overflow: clip;
        }
        .badge {
            position: absolute;
            left: 5px;
            top: 5px;
            width: 6px;
            height: 6px;
            background: var(--badge-color, var(--red-400));
            border-radius: 100%;
        }
    }
}

.mrbilit-date-picker:deep() {
    .month-list {
        padding: 0 6px;
    }
    .main-header-container {
        align-items: flex-end;
        height: 40px;
        padding-block: 6px;
        box-sizing: border-box;
        position: absolute;
        z-index: 1;
    }

    .week-days-container {
        margin-top: 8px;
        height: 32px;
        .week-day-container {
            display: flex;
            align-items: center;
            justify-content: center;
            //flex-basis: $cell-size;
            // text
            @include tg-m($w5, usePalette($p-gray));
        }
    }

    .month-title {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        border-radius: 8px;
        // text
        @include tg-l($w7);
    }

    .month-table-container {
        position: relative;
        width: 346px;
        &:first-child:not(:last-child) {
            padding-left: 10px;
        }
        &:last-child:not(:first-child) {
            padding-right: 10px;
        }
    }
}

@media (max-width: $app-mobile-max-width) {
    .day-wrapper {
        &:hover {
            &::before,
            &::after {
                display: none !important;
            }
        }
    }
    .mrbilit-date-picker.mobile-list :deep() {
        .main-header-container {
            display: none;
        }

        .month-list {
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            overflow-x: hidden;
            padding: 0;
        }

        .month-table-container {
            padding: 8px;
        }

        .month-days-container {
            width: 330px;
        }
    }
}
</style>
